import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type ReadinessTrend = 'improving' | 'stable' | 'declining';

export interface ClientHealthSummary {
  clientId: string;
  organizationId: string;
  clientName: string;
  avgReadiness7d: number | null;
  avgReadiness30d: number | null;
  avgSleepHours7d: number | null;
  avgHrv7d: number | null;
  avgRhr7d: number | null;
  lastSyncAt: string | null;
  daysSinceSync: number | null;
  hasHealthConnection: boolean;
  readinessTrend: ReadinessTrend;
  lastTouchpointDate: string | null;
  daysSinceTouchpoint: number | null;
  touchpointsThisWeek: number;
}

export function useClientHealthSummary(clientId: string | undefined) {
  const [summary, setSummary] = useState<ClientHealthSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    if (!clientId) {
      setSummary(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Query the client_health_summary view
      const { data, error: queryError } = await supabase
        .from('client_health_summary')
        .select('*')
        .eq('client_id', clientId)
        .single();

      if (queryError) {
        // If view doesn't exist yet or no data, return null
        if (queryError.code === 'PGRST116' || queryError.code === '42P01') {
          setSummary(null);
          return;
        }
        throw queryError;
      }

      if (data) {
        setSummary({
          clientId: data.client_id,
          organizationId: data.organization_id,
          clientName: data.client_name,
          avgReadiness7d: data.avg_readiness_7d,
          avgReadiness30d: data.avg_readiness_30d,
          avgSleepHours7d: data.avg_sleep_hours_7d,
          avgHrv7d: data.avg_hrv_7d,
          avgRhr7d: data.avg_rhr_7d,
          lastSyncAt: data.last_sync_at,
          daysSinceSync: data.days_since_sync,
          hasHealthConnection: data.has_health_connection,
          readinessTrend: data.readiness_trend as ReadinessTrend,
          lastTouchpointDate: data.last_touchpoint_date,
          daysSinceTouchpoint: data.days_since_touchpoint,
          touchpointsThisWeek: data.touchpoints_this_week,
        });
      }
    } catch (err) {
      console.error('Error loading client health summary:', err);
      setError('Failed to load health summary');
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  return { summary, loading, error, reload: loadSummary };
}

// Hook to fetch health summaries for multiple clients (for list view)
export function useClientsHealthSummaries(organizationId: string | undefined) {
  const [summaries, setSummaries] = useState<ClientHealthSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSummaries = useCallback(async () => {
    if (!organizationId) {
      setSummaries([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: queryError } = await supabase
        .from('client_health_summary')
        .select('*')
        .eq('organization_id', organizationId);

      if (queryError) {
        // If view doesn't exist yet, return empty
        if (queryError.code === '42P01') {
          setSummaries([]);
          return;
        }
        throw queryError;
      }

      setSummaries(
        (data || []).map((d) => ({
          clientId: d.client_id,
          organizationId: d.organization_id,
          clientName: d.client_name,
          avgReadiness7d: d.avg_readiness_7d,
          avgReadiness30d: d.avg_readiness_30d,
          avgSleepHours7d: d.avg_sleep_hours_7d,
          avgHrv7d: d.avg_hrv_7d,
          avgRhr7d: d.avg_rhr_7d,
          lastSyncAt: d.last_sync_at,
          daysSinceSync: d.days_since_sync,
          hasHealthConnection: d.has_health_connection,
          readinessTrend: d.readiness_trend as ReadinessTrend,
          lastTouchpointDate: d.last_touchpoint_date,
          daysSinceTouchpoint: d.days_since_touchpoint,
          touchpointsThisWeek: d.touchpoints_this_week,
        }))
      );
    } catch (err) {
      console.error('Error loading client health summaries:', err);
      setError('Failed to load health summaries');
      setSummaries([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadSummaries();
  }, [loadSummaries]);

  return { summaries, loading, error, reload: loadSummaries };
}
